import { useEffect, useState } from 'react';
import APIUrls from '../../core/ApiUrls';
import { formatDate, MakeAxiosRequest } from '../../core/utils';
import { RefreshCcw, Trash2, User2Icon } from 'lucide-react';
import Toastify from '../../core/Toastify';
import Modal from '../../components/Modal';

interface Account {
    id: string;
    display_name?: string;
    profile_picture_url?: string;
    status?: string;
    last_synced_at?: string | Date;
    created_at?: string | Date;
    metadata?: {
        connection_status?: string;
        connected_at?: string | Date;
        last_synced_at?: string | Date;
        profile_data?: {
            first_name?: string;
            last_name?: string;
            profile_picture_url?: string;
            occupation?: string;
        };
    };
}

const Accounts = () => {
    const [accounts, setAccounts] = useState<Account[]>();
    const [loading, setLoading] = useState<boolean>(true);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [deleteAccountId, setDeleteAccountId] = useState<string>();
    const [deleting, setDeleting] = useState<boolean>(false);

    const handleConnectAccount = async () => {
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('CONNECT_ACCOUNT'));
            const url = res?.data?.url;
            if (url) {
                window.open(url, 'authPopup', 'width=600,height=700,left=200,top=100');
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleSyncAccount = async (id: string) => {
        const reqBody = {
            accountId: id,
        };
        setSyncing(true);
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('SYNC_ACCOUNT'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Account Sync');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteAccount = async (id: string) => {
        const reqBody = {
            accountId: id,
        };
        setDeleting(true);
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('DISCONNECT_ACCOUNT'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Account Disconnected');
                setDeleteAccountId(undefined);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setDeleting(false);
            handleFetchAccounts();
        }
    };
    const handleFetchAccounts = async () => {
        try {
            const res = await MakeAxiosRequest('GET', APIUrls.getURL('ACCOUNTS'));
            if (res?.status === 200) {
                setAccounts(res?.data.accounts);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        handleFetchAccounts();
    }, []);

    const getStatusBadgeColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'connected':
                return 'bg-green-100 text-green-800';
            case 'pending':
            case 'syncing':
                return 'bg-yellow-100 text-yellow-800';
            case 'error':
            case 'disconnected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="w-full overflow-x-auto">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-xl font-bold text-gray-800">Account Settings</h1>
                <button className="text-sm bg-gradient-purple hover:hover-gradient-purple transition text-white py-3 px-6 rounded-xl font-semibold shadow-sm whitespace-nowrap" onClick={handleConnectAccount}>
                    Connect Account
                </button>
            </div>

            {loading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
            )}

            {!loading &&
                (accounts && accounts.length > 0 ? (
                    <div className="bg-white border-collapse border-[1px] border-gray-300 rounded-xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className=" border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Account</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Connected At</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider pr-6">Last Synced</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider pr-6">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {accounts.map(account => (
                                        <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <div className="flex items-center">
                                                    {account.profile_picture_url || account.metadata?.profile_data?.profile_picture_url ? <img src={account.profile_picture_url || account.metadata?.profile_data?.profile_picture_url} alt={account.display_name} className="h-10 w-10 rounded-full object-cover mr-3 flex-shrink-0" /> : <User2Icon />}
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 truncate">{account.display_name || `${account.metadata?.profile_data?.first_name || ''} ${account.metadata?.profile_data?.last_name || ''}`.trim() || 'Unknown'}</div>
                                                        {account.metadata?.profile_data?.occupation && <div className="text-sm text-gray-500 truncate">{account.metadata.profile_data.occupation}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(account.status || account.metadata?.connection_status || '')}`}>{account.status || account.metadata?.connection_status || 'Unknown'}</span>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatDate(account.metadata?.connected_at || account.created_at)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 pr-6">{formatDate(account.last_synced_at || account.metadata?.last_synced_at)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 flex flex-row gap-2">
                                                <button onClick={() => handleSyncAccount(account.id)} className="bg-gradient-purple hover:hover-gradient-purple transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <RefreshCcw size={16} className={syncing ? 'animate-spin' : ''} />
                                                </button>
                                                <button onClick={() => setDeleteAccountId(account.id)} className="bg-red-600 hover:bg-red-500 transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No accounts connected</h3>
                        <p className="text-gray-500 mb-6">Get started by connecting your first account.</p>
                        <button className="bg-gradient-purple hover:hover-gradient-purple transition text-white py-2 px-6 rounded-xl font-semibold shadow-sm" onClick={handleConnectAccount}>
                            Connect Account
                        </button>
                    </div>
                ))}

            <Modal loading={deleting} isOpen={!!deleteAccountId} onClose={() => setDeleteAccountId(undefined)} onConfirm={() => handleDeleteAccount(deleteAccountId!)} title={`Are you sure you want to delete this Account?`}>
                {deleteAccountId &&
                    (() => {
                        const account = accounts?.find(it => it.id === deleteAccountId);
                        return (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg flex flex-row items-center gap-2">
                                    {account?.profile_picture_url || account?.metadata?.profile_data?.profile_picture_url ? <img src={account?.profile_picture_url || account?.metadata?.profile_data?.profile_picture_url} alt={account.display_name} className="h-10 w-10 rounded-full object-cover mr-3 flex-shrink-0" /> : <User2Icon />}
                                    <p className="text-sm text-gray-600">
                                        <strong>Name:</strong> {account?.display_name || 'Unknown'}
                                    </p>
                                    <span className={`ml-auto px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(account?.status || account?.metadata?.connection_status || '')}`}>{account?.status || account?.metadata?.connection_status || 'Unknown'}</span>
                                </div>
                            </div>
                        );
                    })()}
            </Modal>
        </div>
    );
};

export default Accounts;
